import { Mesh } from 'three'
import type { Object3D } from 'three'

export const mascotShapes = ['basis', 'round', 'cube', 'soft-star'] as const
export type MascotShape = (typeof mascotShapes)[number]

export const keyedMorphs = ['round', 'cube', 'soft-star'] as const
export type KeyedMorph = (typeof keyedMorphs)[number]

export const mascotShapeConfig: Record<MascotShape, { scale: number }> = {
  basis: { scale: 1 },
  round: { scale: 1 },
  cube: { scale: 0.8 },
  'soft-star': { scale: 1 },
}

export type MorphWeights = Record<KeyedMorph, number>

export interface ShapeMorphMesh {
  influences: number[]
  indices: Partial<Record<KeyedMorph, number>>
}

const emptyWeights: MorphWeights = { round: 0, cube: 0, 'soft-star': 0 }

export function isMascotShape(value: string | null): value is MascotShape {
  return value !== null && (mascotShapes as readonly string[]).includes(value)
}

export function nextMascotShape(shape: MascotShape): MascotShape {
  return mascotShapes[(mascotShapes.indexOf(shape) + 1) % mascotShapes.length]
}

export function normalizeMorphName(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function keyedMorphFromName(name: string): KeyedMorph | null {
  const key = normalizeMorphName(name)
  if (key === 'softstar') {
    return 'soft-star'
  }
  if ((keyedMorphs as readonly string[]).includes(key)) {
    return key as KeyedMorph
  }
  return null
}

export function presentationScale(weights: MorphWeights) {
  let scale = 1
  for (const shape of keyedMorphs) {
    scale += (mascotShapeConfig[shape].scale - 1) * weights[shape]
  }
  return scale
}

export function morphWeights(from: MascotShape, to: MascotShape, t: number): MorphWeights {
  const clamped = Math.max(0, Math.min(1, t))
  const eased = clamped * clamped * (3 - 2 * clamped)
  const weights = { ...emptyWeights }
  if (from !== 'basis') {
    weights[from] += 1 - eased
  }
  if (to !== 'basis') {
    weights[to] += eased
  }
  return weights
}

export function collectShapeMorphs(root: Object3D) {
  const meshes: ShapeMorphMesh[] = []
  root.traverse(node => {
    if (!(node instanceof Mesh) || !node.morphTargetInfluences || !node.morphTargetDictionary) {
      return
    }
    const indices: Partial<Record<KeyedMorph, number>> = {}
    for (const [name, index] of Object.entries(node.morphTargetDictionary)) {
      const key = keyedMorphFromName(name)
      if (key && typeof index === 'number') {
        indices[key] = index
      }
    }
    if (Object.keys(indices).length === 0) {
      return
    }
    meshes.push({ influences: node.morphTargetInfluences, indices })
  })
  return meshes
}

export function applyShapeMorphs(
  meshes: readonly ShapeMorphMesh[],
  weights: MorphWeights | undefined,
) {
  if (!weights) {
    return
  }
  for (const mesh of meshes) {
    for (const shape of keyedMorphs) {
      const index = mesh.indices[shape]
      if (index !== undefined) {
        mesh.influences[index] = weights[shape]
      }
    }
  }
}
